// Source-agnostic IFC adapter: turns a parsed IFC model into the
// `ElementView`s the pure core consumes, and orchestrates
// applicability -> requirements per specification.
//
// It never imports an IFC parser. Everything it needs is behind the
// `IfcSource` interface (see `ifcSource.ts`): two methods, both
// synchronous, both trivially implementable over web-ifc, IfcOpenShell
// bindings, a server API, or a fixture in a test. That is what lets the
// same engine run inside a browser viewer and inside a Node CI job.

import type { IdsFile, IdsSpec, Facet } from './idsParse.js'
import type { IfcSource } from './ifcSource.js'
import {
  applicabilityMatches,
  checkRequirement,
  type ClassificationView,
  type ElementFailure,
  type ElementView,
  type IdsReport,
  type PropView,
  type ContainerView,
  type SpecResult,
} from './idsEngine.js'
import { MESSAGES_EN, type IdsMessages } from './idsMessages.js'

// --- Reading STEP values ------------------------------------------------

function unwrap(v: unknown): unknown {
  if (v && typeof v === 'object' && 'value' in (v as object)) {
    return (v as { value: unknown }).value
  }
  return v
}

/** Lê um atributo escalar; '' e nulo viram null (ausente). */
function readScalar(v: unknown): string | null {
  const u = unwrap(v)
  if (u === null || u === undefined) return null
  const s = String(u)
  return s === '' ? null : s
}

/** Como readScalar mas preserva a string vazia (''): distingue um
 *  atributo ausente ($ -> null) de um presente-mas-vazio ('' -> ''). */
function readScalarRaw(v: unknown): string | null {
  const u = unwrap(v)
  if (u === null || u === undefined) return null
  return String(u)
}

function readExpressId(ref: unknown): number | null {
  if (typeof ref === 'number') return ref
  if (ref && typeof ref === 'object') {
    const o = ref as { value?: unknown; expressID?: unknown }
    const cand = o.expressID ?? o.value
    if (typeof cand === 'number') return cand
  }
  return null
}

/** Tipo IFC de um valor tipado. Para valores de medida (IFCLENGTHMEASURE,
 *  IFCLABEL, ...) o web-ifc guarda o nome do tipo no campo `name` e deixa
 *  `type` como marcador interno ('<web-ifc-type-unknown>'); para
 *  entidades resolvidas o nome está em `type`. Aceitamos qualquer um dos
 *  dois quando começa por IFC. null quando não é reconhecível. */
function typeNameOf(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as { type?: unknown; name?: unknown }
  if (typeof o.type === 'string' && /^IFC/i.test(o.type)) {
    return o.type.toUpperCase()
  }
  if (typeof o.name === 'string' && /^IFC/i.test(o.name)) {
    return o.name.toUpperCase()
  }
  return null
}

export interface PropCell {
  value: string | null
  dataType: string | null
}

/** Normaliza um valor "falso" para null (ausente). O IDS considera a
 *  string vazia e o IfcLogical UNKNOWN como não-valores: uma propriedade
 *  com esse valor não satisfaz a existência ("an empty string / a logical
 *  unknown is considered false and will not pass"). */
function normalizeCellValue(
  value: string | null,
  dataType: string | null,
): string | null {
  if (value === null) return null
  const t = value.trim()
  if (t === '') return null
  const bare = t.replace(/^\.|\.$/g, '').toUpperCase()
  if (
    dataType !== null &&
    /LOGICAL/i.test(dataType) &&
    (bare === 'U' || bare === 'UNKNOWN')
  ) {
    return null
  }
  return value
}

/** Lê um valor tipado (IfcValue) para célula. null quando ausente. */
function readTypedCell(raw: unknown): PropCell {
  const u = unwrap(raw)
  const dataType = typeNameOf(raw)
  const value = u === null || u === undefined ? null : String(u)
  return { value: normalizeCellValue(value, dataType), dataType }
}

/** Achata um valor que pode ser um único IfcValue ou uma lista deles em
 *  células (uma por valor). Ignora entradas nulas. */
function readTypedCells(raw: unknown): PropCell[] {
  if (raw === undefined || raw === null) return []
  if (Array.isArray(raw)) {
    const out: PropCell[] = []
    for (const item of raw) {
      const c = readTypedCell(item)
      if (c.value !== null) out.push(c)
    }
    return out
  }
  const c = readTypedCell(raw)
  return c.value === null ? [] : [c]
}

/** Lê as células de valor de uma propriedade IFC. Uma propriedade pode ter
 *  vários valores (Bounded/List/Table/Enumerated); a semântica oficial é
 *  "qualquer valor que corresponda passa" (property/pass-any_matching_...),
 *  por isso emitimos uma célula por valor. Cobre:
 *   - IfcPropertySingleValue.NominalValue
 *   - IfcPropertyBoundedValue: LowerBoundValue, UpperBoundValue, SetPointValue
 *   - IfcPropertyListValue.ListValues
 *   - IfcPropertyTableValue: DefiningValues + DefinedValues
 *   - IfcPropertyEnumeratedValue.EnumerationValues
 *   - IfcPhysicalQuantity: Length/Area/Volume/Count/Weight/TimeValue */
export function readPropCells(po: Record<string, unknown>): PropCell[] {
  if (po.NominalValue !== undefined) return readTypedCells(po.NominalValue)
  // Bounded: qualquer um dos três limites conta como valor presente.
  if (
    po.LowerBoundValue !== undefined ||
    po.UpperBoundValue !== undefined ||
    po.SetPointValue !== undefined
  ) {
    return [
      ...readTypedCells(po.LowerBoundValue),
      ...readTypedCells(po.UpperBoundValue),
      ...readTypedCells(po.SetPointValue),
    ]
  }
  if (po.ListValues !== undefined) return readTypedCells(po.ListValues)
  if (po.EnumerationValues !== undefined) {
    return readTypedCells(po.EnumerationValues)
  }
  if (po.DefiningValues !== undefined || po.DefinedValues !== undefined) {
    return [
      ...readTypedCells(po.DefiningValues),
      ...readTypedCells(po.DefinedValues),
    ]
  }
  const q =
    po.LengthValue ??
    po.AreaValue ??
    po.VolumeValue ??
    po.CountValue ??
    po.WeightValue ??
    po.TimeValue
  if (q !== undefined) return readTypedCells(q)
  return []
}

// --- Conversão de unidades para as unidades-padrão do IDS -------------
//
// O IDS compara valores nas unidades SI base sem prefixo (metro, metro
// quadrado, ...); os valores do IFC vêm nas unidades do modelo
// (IfcUnitAssignment). Convertemos no adaptador, ao indexar, para que o
// núcleo continue a comparar strings puras. Só IfcSIUnit: o prefixo SI
// multiplica linearmente a unidade (spec IFC: magnitude = Prefixo x
// Unidade), por isso o mesmo fator serve length/area/volume. Unidades de
// conversão (imperial) não são convertidas (fator desconhecido -> valor
// fica em cru).

const SI_PREFIX_FACTOR: Record<string, number> = {
  EXA: 1e18, PETA: 1e15, TERA: 1e12, GIGA: 1e9, MEGA: 1e6, KILO: 1e3,
  HECTO: 1e2, DECA: 1e1, DECI: 1e-1, CENTI: 1e-2, MILLI: 1e-3, MICRO: 1e-6,
  NANO: 1e-9, PICO: 1e-12, FEMTO: 1e-15, ATTO: 1e-18,
}

/** Tipos de medida IFC -> IfcSIUnit.UnitType correspondente. */
const MEASURE_UNIT_TYPE: Record<string, string> = {
  IFCLENGTHMEASURE: 'LENGTHUNIT',
  IFCPOSITIVELENGTHMEASURE: 'LENGTHUNIT',
  IFCNONNEGATIVELENGTHMEASURE: 'LENGTHUNIT',
  IFCAREAMEASURE: 'AREAUNIT',
  IFCVOLUMEMEASURE: 'VOLUMEUNIT',
  IFCMASSMEASURE: 'MASSUNIT',
  IFCTIMEMEASURE: 'TIMEUNIT',
  IFCPLANEANGLEMEASURE: 'PLANEANGLEUNIT',
  IFCPOSITIVEPLANEANGLEMEASURE: 'PLANEANGLEUNIT',
  IFCTHERMODYNAMICTEMPERATUREMEASURE: 'THERMODYNAMICTEMPERATUREUNIT',
}

/** measureType (ex.: IFCLENGTHMEASURE) -> fator para a unidade-padrão do
 *  IDS (valor_padrao = valor_modelo x fator). Ausente = sem conversão. */
function buildUnitIndex(src: IfcSource): Map<string, number> {
  const byUnitType = new Map<string, number>()
  let projectIds: number[]
  try {
    projectIds = src.getLineIDsByType('IFCPROJECT')
  } catch {
    projectIds = []
  }
  for (const pid of projectIds) {
    const proj = src.getEntity(pid) as Record<
      string,
      unknown
    > | null
    const uic = proj?.UnitsInContext as Record<string, unknown> | undefined
    const units = uic?.Units
    if (!Array.isArray(units)) continue
    for (const u of units) {
      if (!u || typeof u !== 'object') continue
      const uo = u as Record<string, unknown>
      if (objTypeName(uo) !== 'IFCSIUNIT') continue
      const unitType = readScalar(uo.UnitType)
      if (!unitType) continue
      const prefix = readScalar(uo.Prefix)
      const factor = prefix ? SI_PREFIX_FACTOR[prefix.toUpperCase()] ?? 1 : 1
      byUnitType.set(unitType.toUpperCase(), factor)
    }
  }
  const byMeasure = new Map<string, number>()
  for (const [measure, unitType] of Object.entries(MEASURE_UNIT_TYPE)) {
    const f = byUnitType.get(unitType)
    if (f !== undefined && f !== 1) byMeasure.set(measure, f)
  }
  return byMeasure
}

/** Converte, se possível, o valor de uma célula para a unidade-padrão do
 *  IDS. Só toca em células numéricas de tipo de medida conhecido. */
function convertCellUnit(cell: PropCell, units: Map<string, number>): PropCell {
  if (cell.value === null || cell.dataType === null) return cell
  const factor = units.get(cell.dataType.toUpperCase())
  if (factor === undefined) return cell
  const n = Number(cell.value)
  if (!Number.isFinite(n)) return cell
  return { value: String(n * factor), dataType: cell.dataType }
}

/** Atributos-raiz de um IfcPreDefinedPropertySet que não são
 *  propriedades (herdados de IfcRoot / metadados do parser). */
const PREDEFINED_PSET_SKIP = new Set([
  'expressID',
  'type',
  'GlobalId',
  'OwnerHistory',
  'Name',
  'Description',
])

/** Emite os atributos escalares/enum de um conjunto de propriedades
 *  predefinido como propriedades (nome = atributo, valor = valor). */
function collectPredefinedPset(
  pset: Record<string, unknown>,
  push: (name: string, cell: PropCell) => void,
): void {
  const t = objTypeName(pset)
  if (t === 'IFCPROPERTYSET' || t === 'IFCELEMENTQUANTITY') return
  for (const key of Object.keys(pset)) {
    if (PREDEFINED_PSET_SKIP.has(key)) continue
    const raw = pset[key]
    if (raw === null || raw === undefined) continue
    // Só valores escalares/enum; referências e listas não são propriedades
    // simples de valor.
    if (Array.isArray(raw)) continue
    const u = unwrap(raw)
    if (u === null || u === undefined || typeof u === 'object') continue
    push(key, readTypedCell(raw))
  }
}

/** Achata um conjunto de propriedades (IfcPropertySet, IfcElementQuantity
 *  ou pset predefinido) em linhas PropView, convertendo unidades. */
function psetRows(
  pset: Record<string, unknown>,
  units: Map<string, number>,
): PropView[] {
  const psetName = readScalar(pset.Name) ?? '(sem nome)'
  const rows: PropView[] = []
  const push = (name: string, cell: PropCell) => {
    const c = convertCellUnit(cell, units)
    rows.push({ pset: psetName, name, value: c.value, dataType: c.dataType })
  }
  const collect = (arr: unknown) => {
    if (!Array.isArray(arr)) return
    for (const p of arr) {
      if (!p || typeof p !== 'object') continue
      const po = p as Record<string, unknown>
      const nm = readScalar(po.Name)
      if (!nm) continue
      const cells = readPropCells(po)
      // Sem células = propriedade sem valor: guardamos uma linha nula para
      // os requisitos de só-existência continuarem a ver a propriedade.
      if (cells.length === 0) push(nm, { value: null, dataType: null })
      else for (const cell of cells) push(nm, cell)
    }
  }
  collect(pset.HasProperties)
  collect(pset.Quantities)
  // Conjuntos de propriedades predefinidos (IfcDoorPanelProperties, ...):
  // não têm HasProperties; os atributos enum/escalares SÃO as
  // propriedades. Suportado mas desencorajado pela norma.
  if (
    rows.length === 0 &&
    pset.HasProperties === undefined &&
    pset.Quantities === undefined
  ) {
    collectPredefinedPset(pset, push)
  }
  return rows
}

/** expressID -> lista de propriedades (pset, name, value). Cobre as
 *  propriedades de ocorrência (IfcRelDefinesByProperties) e as dos
 *  IfcTypeObject via o atributo HasPropertySets (indexadas sob o
 *  expressID do tipo, para herança em `buildView`). Converte valores de
 *  medida para as unidades-padrão do IDS via `units`. */
function buildPropIndex(
  src: IfcSource,
  units: Map<string, number>,
  typeIds: Iterable<number>,
): Map<number, PropView[]> {
  const index = new Map<number, PropView[]>()
  const addRows = (eid: number, rows: PropView[]) => {
    if (rows.length === 0) return
    const cur = index.get(eid)
    if (cur) cur.push(...rows)
    else index.set(eid, [...rows])
  }
  let relIds: number[]
  try {
    relIds = src.getLineIDsByType('IFCRELDEFINESBYPROPERTIES')
  } catch {
    relIds = []
  }
  for (const relId of relIds) {
    const rel = src.getEntity(relId) as Record<
      string,
      unknown
    > | null
    if (!rel) continue
    const pdef = rel.RelatingPropertyDefinition
    if (!pdef || typeof pdef !== 'object') continue
    const rows = psetRows(pdef as Record<string, unknown>, units)
    if (rows.length === 0) continue
    const related = rel.RelatedObjects
    if (!Array.isArray(related)) continue
    for (const ro of related) {
      const eid = readExpressId(ro)
      if (eid != null) addRows(eid, rows)
    }
  }
  // Propriedades dos tipos: IfcTypeObject.HasPropertySets (atributo, não
  // relação) -> indexadas sob o expressID do tipo para herança.
  for (const typeId of typeIds) {
    const t = src.getEntity(typeId) as Record<
      string,
      unknown
    > | null
    const psets = t?.HasPropertySets
    if (!Array.isArray(psets)) continue
    for (const ps of psets) {
      if (ps && typeof ps === 'object') {
        addRows(typeId, psetRows(ps as Record<string, unknown>, units))
      }
    }
  }
  // Propriedades de materiais: o IDS trata o IfcMaterial como entidade
  // aplicável, e as suas propriedades vivem em IfcMaterialProperties
  // (IFC4: Name, Description, Properties, Material) ou em
  // IfcExtendedMaterialProperties (IFC2X3: Material, ExtendedProperties,
  // Description, Name). Indexadas sob o expressID do material.
  for (const cls of ['IFCMATERIALPROPERTIES', 'IFCEXTENDEDMATERIALPROPERTIES']) {
    let ids: number[]
    try {
      ids = src.getLineIDsByType(cls)
    } catch {
      ids = []
    }
    for (const id of ids) {
      const mp = src.getEntity(id) as Record<string, unknown> | null
      if (!mp) continue
      const mid = readExpressId(mp.Material)
      if (mid == null) continue
      addRows(
        mid,
        psetRows(
          { Name: mp.Name, HasProperties: mp.Properties ?? mp.ExtendedProperties },
          units,
        ),
      )
    }
  }
  return index
}

/** Classes de ocorrência genéricas do IFC2X3 (o esquema não tem
 *  IfcAirTerminal, só IfcFlowTerminal com um IfcAirTerminalType). A
 *  tabela de mapeamento de tipos do IDS diz que, nesses casos, a
 *  entidade a verificar é a do tipo sem o sufixo "Type". Em IFC4 estas
 *  classes são abstractas ou raras, por isso aplicar sempre é seguro. */
const CLASSES_GENERICAS_2X3 = new Set([
  'IFCFLOWTERMINAL',
  'IFCFLOWSEGMENT',
  'IFCFLOWFITTING',
  'IFCFLOWCONTROLLER',
  'IFCFLOWMOVINGDEVICE',
  'IFCFLOWSTORAGEDEVICE',
  'IFCFLOWTREATMENTDEVICE',
  'IFCENERGYCONVERSIONDEVICE',
  'IFCDISTRIBUTIONCONTROLELEMENT',
  'IFCDISTRIBUTIONFLOWELEMENT',
  'IFCDISTRIBUTIONELEMENT',
  'IFCDISTRIBUTIONCHAMBERELEMENT',
  'IFCFURNISHINGELEMENT',
  'IFCELECTRICALELEMENT',
  'IFCEQUIPMENTELEMENT',
])

/** Classe efectiva -> ocorrências que a tabela de mapeamento lhe dá
 *  (ex.: IFCAIRTERMINAL -> os IfcFlowTerminal tipados por
 *  IfcAirTerminalType). */
function buildMappedIndex(src: IfcSource, types: Map<number, number>): Map<string, number[]> {
  const index = new Map<string, number[]>()
  for (const [eid, typeId] of types) {
    const occ = objTypeName(src.getEntity(eid))
    if (!occ || !CLASSES_GENERICAS_2X3.has(occ)) continue
    const tcls = objTypeName(src.getEntity(typeId))
    if (!tcls || !tcls.endsWith('TYPE')) continue
    const efectiva = tcls.slice(0, -4)
    if (efectiva === occ) continue
    const lista = index.get(efectiva)
    if (lista) lista.push(eid)
    else index.set(efectiva, [eid])
  }
  return index
}

/** Nome de classe IFC de um ref já resolvido (o rawIfc reescreve
 *  o `.type` numérico para o nome). */
function objTypeName(o: unknown): string | null {
  if (o && typeof o === 'object' && 'type' in (o as object)) {
    const t = (o as { type?: unknown }).type
    if (typeof t === 'string' && t) return t.toUpperCase()
  }
  return null
}

/** expressID -> classificações (system, code), sobre
 *  IfcRelAssociatesClassification e (para recursos não-rooted como
 *  IfcMaterial) IfcExternalReferenceRelationship. */
function classificationViewsOf(ref: Record<string, unknown>): ClassificationView[] {
  // Códigos da cadeia de subreferências: a referência-folha e os
  // Identification de todos os ancestrais (ReferencedSource). Assim, uma
  // classificação "cheia" EF_25_10_25 -> EF_25_10 -> ... corresponde
  // quando o IDS pede um código ancestral (EF_25_10). Ver doc oficial.
  const values = new Set<string>()
  const leaf =
    readScalar(ref.Identification) ??
    readScalar(ref.ItemReference) ??
    readScalar(ref.Name)
  if (leaf) values.add(leaf)
  // Sistema: o Name da IfcClassification no topo da cadeia
  // ReferencedSource; se a referência não tem source, o seu Name é o
  // sistema.
  let system: string | null = null
  let node: Record<string, unknown> | null = ref
  for (let i = 0; i < 10 && node; i++) {
    const src = node.ReferencedSource
    if (src && typeof src === 'object') {
      node = src as Record<string, unknown>
      const id = readScalar(node.Identification) ?? readScalar(node.ItemReference)
      if (id) values.add(id)
      system = readScalar(node.Name) ?? system
    } else break
  }
  if (system === null) system = readScalar(ref.Name)
  const views: ClassificationView[] = [...values].map((value) => ({
    system,
    value,
  }))
  if (views.length === 0) views.push({ system, value: null })
  return views
}

function buildClassificationIndex(
  src: IfcSource,
): Map<number, ClassificationView[]> {
  const index = new Map<number, ClassificationView[]>()
  const add = (targets: unknown, views: ClassificationView[]) => {
    if (!Array.isArray(targets)) return
    for (const ro of targets) {
      const eid = readExpressId(ro)
      if (eid == null) continue
      const cur = index.get(eid)
      if (cur) cur.push(...views)
      else index.set(eid, [...views])
    }
  }
  const eachRel = (relType: string, fn: (rel: Record<string, unknown>) => void) => {
    let relIds: number[]
    try {
      relIds = src.getLineIDsByType(relType)
    } catch {
      return
    }
    for (const relId of relIds) {
      const rel = src.getEntity(relId) as Record<
        string,
        unknown
      > | null
      if (rel) fn(rel)
    }
  }
  // Elementos rooted: IfcRelAssociatesClassification.
  eachRel('IFCRELASSOCIATESCLASSIFICATION', (rel) => {
    const rc = rel.RelatingClassification
    if (!rc || typeof rc !== 'object') return
    add(rel.RelatedObjects, classificationViewsOf(rc as Record<string, unknown>))
  })
  // Recursos não-rooted (IfcMaterial, IfcProfileDef, ...):
  // IfcExternalReferenceRelationship com uma IfcClassificationReference.
  eachRel('IFCEXTERNALREFERENCERELATIONSHIP', (rel) => {
    const rr = rel.RelatingReference
    if (!rr || typeof rr !== 'object') return
    if (objTypeName(rr) !== 'IFCCLASSIFICATIONREFERENCE') return
    // IFC4: RelatedResourceObjects; toleramos também RelatedResources.
    add(
      rel.RelatedResourceObjects ?? rel.RelatedResources,
      classificationViewsOf(rr as Record<string, unknown>),
    )
  })
  return index
}

/** Recolhe os nomes E categorias de material de uma estrutura
 *  (IfcMaterial, LayerSet, ConstituentSet, ProfileSet, List, Usage). O
 *  facet material do IDS compara o `value` contra o nome do material, a
 *  sua Category, e o nome dos conjuntos (LayerSetName / Name do
 *  profile/constituent set): qualquer um que corresponda passa. */
function materialNames(m: unknown): string[] {
  const out = new Set<string>()
  const visit = (o: unknown, depth: number) => {
    if (!o || typeof o !== 'object' || depth > 6) return
    const r = o as Record<string, unknown>
    // Nome do material / do constituent / do profile / do profile-set.
    const nm = readScalar(r.Name)
    if (nm) out.add(nm)
    // Nome de um IfcMaterialLayerSet (atributo LayerSetName, não Name).
    const lsn = readScalar(r.LayerSetName)
    if (lsn) out.add(lsn)
    // Category do material / layer / constituent / profile.
    const cat = readScalar(r.Category)
    if (cat) out.add(cat)
    for (const k of [
      'Material',
      'Materials',
      'MaterialLayers',
      'MaterialConstituents',
      'MaterialProfiles',
      'ForLayerSet',
      'ForProfileSet',
    ]) {
      const v = r[k]
      if (Array.isArray(v)) v.forEach((x) => visit(x, depth + 1))
      else if (v && typeof v === 'object') visit(v, depth + 1)
    }
  }
  visit(m, 0)
  return [...out]
}

/** expressID -> nomes de materiais, sobre IfcRelAssociatesMaterial. */
function buildMaterialIndex(src: IfcSource): Map<number, string[]> {
  const index = new Map<number, string[]>()
  let relIds: number[]
  try {
    relIds = src.getLineIDsByType('IFCRELASSOCIATESMATERIAL')
  } catch {
    return index
  }
  for (const relId of relIds) {
    const rel = src.getEntity(relId) as Record<
      string,
      unknown
    > | null
    if (!rel) continue
    const names = materialNames(rel.RelatingMaterial)
    if (names.length === 0) continue
    const related = rel.RelatedObjects
    if (!Array.isArray(related)) continue
    for (const ro of related) {
      const eid = readExpressId(ro)
      if (eid == null) continue
      const cur = index.get(eid)
      if (cur) cur.push(...names)
      else index.set(eid, [...names])
    }
  }
  return index
}

/** expressID de uma ocorrência -> expressID do seu IfcTypeObject, sobre
 *  IfcRelDefinesByType. Suporta a herança (materiais, propriedades,
 *  classificações, predefinedType) da ocorrência a partir do tipo. */
function buildTypeIndex(src: IfcSource): Map<number, number> {
  const index = new Map<number, number>()
  let relIds: number[]
  try {
    relIds = src.getLineIDsByType('IFCRELDEFINESBYTYPE')
  } catch {
    return index
  }
  for (const relId of relIds) {
    const rel = src.getEntity(relId) as Record<
      string,
      unknown
    > | null
    if (!rel) continue
    const typeId = readExpressId(rel.RelatingType)
    if (typeId == null) continue
    const related = rel.RelatedObjects
    if (!Array.isArray(related)) continue
    for (const ro of related) {
      const eid = readExpressId(ro)
      if (eid != null) index.set(eid, typeId)
    }
  }
  return index
}

/** expressID -> contentores (classe + relação + predefinedType), sobre
 *  IfcRelAggregates, IfcRelNests, IfcRelContainedInSpatialStructure e
 *  IfcRelAssignsToGroup. As relações de decomposição são TRANSITIVAS: um
 *  elemento é parte de qualquer ancestral na cadeia (agregação/nesting
 *  indiretos, contentor indireto). A relação é seguida de forma pura por
 *  tipo (a cadeia de agregação dá ancestrais por agregação, etc.). */
function buildContainerIndex(src: IfcSource): Map<number, ContainerView[]> {
  // Arestas filho -> pais, por tipo de relação.
  const collectEdges = (
    relType: string,
    relatingKey: string,
    partsKey: string,
  ): Map<number, Set<number>> => {
    const childToParents = new Map<number, Set<number>>()
    let relIds: number[]
    try {
      relIds = src.getLineIDsByType(relType)
    } catch {
      return childToParents
    }
    for (const relId of relIds) {
      const rel = src.getEntity(relId) as Record<
        string,
        unknown
      > | null
      if (!rel) continue
      const parentId = readExpressId(rel[relatingKey])
      if (parentId == null) continue
      const parts = rel[partsKey]
      if (!Array.isArray(parts)) continue
      for (const ro of parts) {
        const childId = readExpressId(ro)
        if (childId == null) continue
        let set = childToParents.get(childId)
        if (!set) childToParents.set(childId, (set = new Set()))
        set.add(parentId)
      }
    }
    return childToParents
  }

  // Metadados (classe + predefinedType resolvido) de um contentor, em
  // cache para não repetir getEntity.
  const metaCache = new Map<number, { ifcClass: string; predefinedType: string | null } | null>()
  const metaOf = (id: number) => {
    if (metaCache.has(id)) return metaCache.get(id)!
    const e = src.getEntity(id) as Record<string, unknown> | null
    const cls = objTypeName(e)
    const meta = cls
      ? { ifcClass: cls, predefinedType: resolvePredef(e) }
      : null
    metaCache.set(id, meta)
    return meta
  }

  const index = new Map<number, ContainerView[]>()
  const emit = (childId: number, ancestorId: number, relation: string) => {
    const meta = metaOf(ancestorId)
    if (!meta) return
    const v: ContainerView = {
      ifcClass: meta.ifcClass,
      relation,
      predefinedType: meta.predefinedType,
    }
    const cur = index.get(childId)
    if (cur) cur.push(v)
    else index.set(childId, [v])
  }

  // Fecho transitivo puro por relação: para cada filho, todos os
  // ancestrais alcançáveis seguindo essa relação.
  const addTransitive = (
    childToParents: Map<number, Set<number>>,
    relation: string,
    transitive: boolean,
  ) => {
    for (const child of childToParents.keys()) {
      const seen = new Set<number>()
      const stack = [...(childToParents.get(child) ?? [])]
      while (stack.length > 0) {
        const anc = stack.pop()!
        if (seen.has(anc)) continue
        seen.add(anc)
        emit(child, anc, relation)
        if (transitive) {
          const grand = childToParents.get(anc)
          if (grand) for (const g of grand) if (!seen.has(g)) stack.push(g)
        }
      }
    }
  }

  addTransitive(
    collectEdges('IFCRELAGGREGATES', 'RelatingObject', 'RelatedObjects'),
    'IFCRELAGGREGATES',
    true,
  )
  addTransitive(
    collectEdges('IFCRELNESTS', 'RelatingObject', 'RelatedObjects'),
    'IFCRELNESTS',
    true,
  )
  addTransitive(
    collectEdges(
      'IFCRELCONTAINEDINSPATIALSTRUCTURE',
      'RelatingStructure',
      'RelatedElements',
    ),
    'IFCRELCONTAINEDINSPATIALSTRUCTURE',
    true,
  )
  addTransitive(
    collectEdges('IFCRELASSIGNSTOGROUP', 'RelatingGroup', 'RelatedObjects'),
    'IFCRELASSIGNSTOGROUP',
    true,
  )
  return index
}

/** Nomes de classe IFC a que a applicability se refere, ou null quando
 *  a applicability não pode ser resolvida a classes (sem entity facet,
 *  ou entity com pattern/bounds). */
function resolveApplicableClasses(spec: IdsSpec): string[] | null {
  const entityFacets = spec.applicability.filter((f) => f.type === 'entity')
  if (entityFacets.length === 0) return null
  const classes = new Set<string>()
  for (const f of entityFacets) {
    if (f.type !== 'entity') continue
    if (f.name.simpleValue !== undefined) {
      classes.add(f.name.simpleValue.toUpperCase())
    } else if (f.name.enumeration && f.name.enumeration.length > 0) {
      for (const c of f.name.enumeration) classes.add(c.toUpperCase())
    } else {
      // pattern/bounds na classe: precisaria de enumerar todos os tipos
      // do modelo (E1.4).
      return null
    }
  }
  return [...classes]
}

/** Índices do modelo de que a construção de uma `ElementView` depende.
 *  Exportado (como `IdsIndexes`) para a extensão de regras os reutilizar
 *  em vez de os voltar a construir. */
export interface Indexes {
  props: Map<number, PropView[]>
  classifications: Map<number, ClassificationView[]>
  materials: Map<number, string[]>
  containers: Map<number, ContainerView[]>
  /** ocorrência -> IfcTypeObject (IfcRelDefinesByType). */
  types: Map<number, number>
  /** expressID do tipo -> predefinedType já resolvido (USERDEFINED ->
   *  ElementType/ObjectType), para herança. */
  typePredef: Map<number, string | null>
  /** Classe efectiva pela tabela de mapeamento de tipos do IFC2X3 ->
   *  ocorrências (ver `buildMappedIndex`). */
  mapped: Map<string, number[]>
}

export type IdsIndexes = Indexes

/** Resolve o predefinedType efetivo de uma entidade: quando é
 *  USERDEFINED, o valor real está no ObjectType (ocorrência),
 *  ElementType (IfcTypeProduct) ou ProcessType (IfcTypeProcess). Devolve
 *  null quando ausente. */
function resolvePredef(e: Record<string, unknown> | null): string | null {
  if (!e) return null
  const pt = readScalar(e.PredefinedType)
  if (pt !== null && pt.replace(/\./g, '').toUpperCase() === 'USERDEFINED') {
    return (
      readScalar(e.ObjectType) ??
      readScalar(e.ElementType) ??
      readScalar(e.ProcessType) ??
      pt
    )
  }
  return pt
}

/** Junta os dados diretos da ocorrência com os herdados do seu tipo
 *  (materiais, propriedades, classificações). A ocorrência tem
 *  precedência na ordem, mas ambos contam para a existência. */
function inherited<T>(
  index: Map<number, T[]>,
  expressID: number,
  typeId: number | undefined,
): T[] {
  const own = index.get(expressID) ?? []
  if (typeId === undefined) return own
  const fromType = index.get(typeId) ?? []
  if (fromType.length === 0) return own
  return [...own, ...fromType]
}

export function buildView(
  src: IfcSource,
  expressID: number,
  ifcClass: string,
  idx: Indexes,
): ElementView {
  const e = src.getEntity(expressID) as Record<
    string,
    unknown
  > | null
  const attribute = (name: string) => (e ? readScalar(e[name]) : null)
  const attributeRaw = (name: string) => (e ? readScalarRaw(e[name]) : null)
  const typeId = idx.types.get(expressID)
  // predefinedType efetivo: o da ocorrência (USERDEFINED resolvido);
  // se ausente, herdado do IfcTypeObject.
  let predefinedType = resolvePredef(e)
  let predefinedTypeRaw = attributeRaw('PredefinedType')
  if (predefinedType === null && typeId !== undefined) {
    predefinedType = idx.typePredef.get(typeId) ?? null
    // O literal também vem do tipo: uma ocorrência IFC2X3 sem
    // PredefinedType, tipada por um tipo USERDEFINED, tem de passar num
    // facet que peça "USERDEFINED".
    const t = src.getEntity(typeId) as Record<string, unknown> | null
    predefinedTypeRaw = t ? readScalar(t.PredefinedType) : null
  }
  return {
    ifcClass,
    globalId: attribute('GlobalId'),
    name: attribute('Name'),
    predefinedType,
    predefinedTypeRaw,
    attribute,
    attributeRaw,
    properties: inherited(idx.props, expressID, typeId),
    classifications: inherited(idx.classifications, expressID, typeId),
    materials: inherited(idx.materials, expressID, typeId),
    containers: idx.containers.get(expressID) ?? [],
  }
}

/** Rótulo curto de um facet para a lista de "não avaliados". */
function facetLabel(f: Facet): string {
  return f.type
}

/** Constrói os índices de um modelo (propriedades, classificações,
 *  materiais, contentores, tipos), carregando o parser paralelo se ainda
 *  não estiver. Partilhado pelo `evaluateIds` e pela extensão de regras
 *  condicionais, para o trabalho pesado se fazer uma vez só. */
export async function buildIdsIndexes(
  src: IfcSource,
  onProgress?: (step: string) => void,
  m: IdsMessages = MESSAGES_EN,
): Promise<Indexes> {
  onProgress?.(m.progressIndexing)
  const units = buildUnitIndex(src)
  const types = buildTypeIndex(src)
  const typeIds = new Set(types.values())
  const typePredef = new Map<number, string | null>()
  for (const typeId of typeIds) {
    typePredef.set(
      typeId,
      resolvePredef(
        src.getEntity(typeId) as Record<string, unknown> | null,
      ),
    )
  }
  return {
    props: buildPropIndex(src, units, typeIds),
    classifications: buildClassificationIndex(src),
    materials: buildMaterialIndex(src),
    containers: buildContainerIndex(src),
    types,
    typePredef,
    mapped: buildMappedIndex(src, types),
  }
}

/** Avalia um modelo contra um IDS. Carrega o parser paralelo on-demand
 *  (segundos em modelos grandes na primeira vez). */
export async function evaluateIds(
  src: IfcSource,
  ids: IdsFile,
  onProgress?: (step: string) => void,
  m: IdsMessages = MESSAGES_EN,
): Promise<IdsReport> {
  const idx = await buildIdsIndexes(src, onProgress, m)

  const results: SpecResult[] = []
  for (let s = 0; s < ids.specs.length; s++) {
    const spec = ids.specs[s]
    onProgress?.(
      m.progressValidating(s + 1, ids.specs.length, spec.name ?? 'spec'),
    )
    const classes = resolveApplicableClasses(spec)
    if (classes === null) {
      results.push({
        spec,
        status: 'unresolved',
        applicableCount: 0,
        passCount: 0,
        passExpressIDs: [],
        failures: [],
        unsupportedRequirements: [],
        note: m.noteApplicabilityNotConcrete,
      })
      continue
    }

    // Requisitos não avaliáveis (fase 1): classification, material,
    // partOf, e attribute com nome não-simples.
    const unsupportedReqs = new Set<string>()

    let applicableCount = 0
    let passCount = 0
    const passExpressIDs: number[] = []
    const failures: ElementFailure[] = []

    for (const cls of classes) {
      let expressIDs: number[]
      try {
        expressIDs = src.getLineIDsByType(cls)
      } catch {
        expressIDs = []
      }
      const mapeadas = idx.mapped.get(cls)
      if (mapeadas && mapeadas.length > 0) {
        const vistos = new Set(expressIDs)
        expressIDs = [...expressIDs, ...mapeadas.filter((id) => !vistos.has(id))]
      }
      for (const expressID of expressIDs) {
        const view = buildView(src, expressID, cls, idx)
        if (!applicabilityMatches(spec.applicability, view)) continue
        applicableCount++
        const reasons: string[] = []
        for (const req of spec.requirements) {
          const check = checkRequirement(req, view, m)
          if (!check.supported) {
            unsupportedReqs.add(facetLabel(req))
            continue
          }
          if (check.reason) reasons.push(check.reason)
        }
        if (reasons.length === 0) {
          passCount++
          passExpressIDs.push(expressID)
        } else {
          failures.push({
            expressID,
            globalId: view.globalId,
            ifcClass: cls,
            name: view.name,
            reasons,
          })
        }
      }
    }

    // Cardinalidade ao nível da specification (minOccurs/maxOccurs no
    // <applicability>):
    //  - prohibited (maxOccurs=0): nenhuma entidade pode ser aplicável;
    //    e é IDS inválido se tiver requisitos.
    //  - required (default): tem de haver >=1 entidade aplicável.
    //  - optional (minOccurs=0): sem restrição sobre a contagem.
    let specNote: string | undefined
    let specLevelFail = false
    if (spec.applicabilityCardinality === 'prohibited') {
      if (spec.requirements.length > 0) {
        specLevelFail = true
        specNote =
          'Invalid IDS: a prohibited specification (maxOccurs=0) cannot ' +
          'carry requirements.'
      } else if (applicableCount > 0) {
        specLevelFail = true
        specNote =
          `Prohibited specification, but ${applicableCount} entities ` +
          'match its applicability.'
      }
    } else if (
      spec.applicabilityCardinality === 'required' &&
      applicableCount === 0
    ) {
      specLevelFail = true
      specNote =
        'Required specification, but no entity matches its applicability.'
    }

    results.push({
      spec,
      status:
        failures.length === 0 && !specLevelFail ? 'ok' : 'has-failures',
      applicableCount,
      passCount,
      passExpressIDs,
      failures,
      unsupportedRequirements: [...unsupportedReqs],
      note: specNote,
    })
  }

  return { results }
}
